import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onIdTokenChanged, signOut, type User } from '@/lib/authClient';
import { auth } from '../lib/firebase';
import { isUserRole, type UserRole } from './roles';

// Re-export UserRole for consumers that expect it from AuthContext
export type { UserRole } from './roles';
import { logger } from '@/lib/logger';

export interface UserClaims {
  uid?: string;
  sub?: string;
  role?: UserRole;
  tenantId?: string;
  employeeId?: string; // Employee document ID (set during invite redemption)
  owner?: boolean;
  admin?: boolean;
  member?: boolean;
  cmsRole?: 'admin' | 'editor' | 'viewer'; // CMS access role
  superAdmin?: boolean; // Platform super-admin (EtchAre owners)
}

type AuthState = {
  user: User | null;
  claims: UserClaims | null;
  loading: boolean;
};

export type AuthContextValue = AuthState & {
  refreshClaims: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  claims: null,
  loading: true,
  refreshClaims: async () => { }
});

async function loadClaims(user: User | null): Promise<UserClaims | null> {
  if (!user) {
    return null;
  }

  // DO NOT force-refresh here. onIdTokenChanged already fires when the token
  // changes, so we just read the current token result. Force-refreshing inside
  // the listener creates a feedback loop: getIdToken(true) → SDK fires
  // onIdTokenChanged → loadClaims → getIdToken(true) → infinite loop.
  // If the token is stale, Firebase SDK auto-refreshes ~5 min before expiry.
  const token = await user.getIdTokenResult();
  const { owner, admin, member, tenantId, role, sub, cmsRole, superAdmin, employeeId } = token.claims as Record<string, unknown>;

  const normalizedRole = isUserRole(role) ? role : null;

  const resolvedOwner = Boolean(owner) || normalizedRole === 'owner';
  const resolvedAdmin = Boolean(admin) || normalizedRole === 'hr_admin' || normalizedRole === 'manager';
  const resolvedMember =
    Boolean(member) || (normalizedRole !== null && normalizedRole !== 'owner');

  // Validate cmsRole (admin, editor, or viewer)
  const normalizedCmsRole =
    cmsRole === 'admin' || cmsRole === 'editor' || cmsRole === 'viewer' ? cmsRole : undefined;

  return {
    uid: user.uid,
    sub: typeof sub === 'string' ? sub : user.uid,
    owner: resolvedOwner,
    admin: resolvedAdmin,
    member: resolvedMember,
    tenantId: typeof tenantId === 'string' ? tenantId : undefined,
    employeeId: typeof employeeId === 'string' ? employeeId : undefined,
    role: normalizedRole ?? undefined,
    cmsRole: normalizedCmsRole,
    superAdmin: Boolean(superAdmin)
  } satisfies UserClaims;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({ user: null, claims: null, loading: true });

  // Circuit breaker: track consecutive auth failures to prevent infinite loops.
  // When token refresh fails, Firebase SDK fires onIdTokenChanged again, which
  // can create a tight loop that hammers securetoken.googleapis.com until
  // auth/quota-exceeded is hit. After MAX_AUTH_FAILURES consecutive failures,
  // we sign out cleanly instead of retrying forever.
  const authFailureCount = useRef(0);
  const AUTH_MAX_FAILURES = 3;

  const refreshClaims = useCallback(async () => {
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    try {
      // Force refresh here since this is an explicit user action, not a listener
      await currentUser.getIdToken(true);
      const claims = await loadClaims(currentUser);
      authFailureCount.current = 0;
      setState({ user: currentUser, claims, loading: false });
    } catch (error) {
      logger.error('Failed to refresh auth claims', error);
      setState((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    // Note: In development with React StrictMode, this effect runs twice (double-mounting)
    // This is expected behavior for detecting side effects - not a bug
    // The cleanup function properly unsubscribes the listener
    const unsubscribe = onIdTokenChanged(async (nextUser) => {
      logger.debug('[AuthContext] onIdTokenChanged fired', { uid: nextUser?.uid });

      try {
        const claims = await loadClaims(nextUser);
        logger.debug('[AuthContext] Claims loaded', { claims });

        if (!isMounted) {
          return;
        }

        // Reset failure counter on success
        authFailureCount.current = 0;

        // CRITICAL FIX: Only update state if user/claims actually changed
        // Prevents excessive re-renders that cause Firestore listener churn
        setState((prev) => {
          const userChanged = prev.user?.uid !== nextUser?.uid;
          const claimsChanged = JSON.stringify(prev.claims) !== JSON.stringify(claims);

          if (!userChanged && !claimsChanged && !prev.loading) {
            logger.debug('[AuthContext] No changes detected, skipping state update');
            return prev;
          }

          return { user: nextUser, claims, loading: false };
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        authFailureCount.current += 1;
        logger.error(`Failed to load auth state (attempt ${authFailureCount.current}/${AUTH_MAX_FAILURES})`, error);

        // Circuit breaker: after repeated failures, sign out to stop the loop.
        // This prevents infinite token refresh cycles that burn through Firebase
        // Auth quotas and rack up costs.
        if (authFailureCount.current >= AUTH_MAX_FAILURES) {
          logger.error('[AuthContext] Circuit breaker triggered — signing out to stop auth loop');
          authFailureCount.current = 0;
          try {
            await signOut(auth);
          } catch {
            // Ignore sign-out errors — we're already in a broken state
          }
          setState({ user: null, claims: null, loading: false });
          return;
        }

        setState({ user: nextUser, claims: null, loading: false });
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);



  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Type definition for window extension
    interface CustomWindow extends Window {
      Cypress?: unknown;
      __appAuthSetState?: (next: AuthState) => void;
      authState?: AuthContextValue;
    }

    const win = window as CustomWindow;

    if (!win.Cypress) {
      return;
    }

    win.__appAuthSetState = (next: AuthState) => {
      setState(next);
    };

    return () => {
      delete win.__appAuthSetState;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => {
      const val = { ...state, refreshClaims };

      if (typeof window !== 'undefined') {
        const win = window as { Cypress?: unknown; authState?: AuthContextValue };
        if (win.Cypress) {
          win.authState = val;
        }
      }
      return val;
    },
    [state, refreshClaims]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export { AuthContext };
